import { useApiClient } from '@klareospace/pharos-lib-front-utils';
import type { ImportFileDownloadUrl, ImportHistoryItem } from '@klareospace/pharos-lib-types';
import { createMutationCallbacks, downloadFromUrl } from '@klareospace/pharos-lib-web-utils';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { ImportsApi } from '../api';
import { createImportsApi } from '../api';

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Resolves a signed download URL from the API, then triggers the browser download by anchor
 * navigation to that URL. Silent on success — only toasts on failure. `TVariables` defaults to
 * `ImportHistoryItem`; override it when downloading by something other than an import row.
 */
export function useImportBlobDownload<TVariables = ImportHistoryItem>(
  resolveUrl: (api: ImportsApi, variables: TVariables) => Promise<ImportFileDownloadUrl>,
  errorKey: string,
): UseMutationResult<void, Error, TVariables> {
  const client = useApiClient('deliveries');
  const api = createImportsApi(client);
  const { t } = useTranslation('imports');

  return useMutation<void, Error, TVariables>({
    mutationFn: async (variables: TVariables): Promise<void> => {
      const { url } = await resolveUrl(api, variables);
      downloadFromUrl(url);
    },
    ...createMutationCallbacks<void, TVariables>({ errorKey, t }),
  });
}
